import { useEffect, useState } from 'react';
import { Monitor, Download, Loader2, FlaskConical } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';

/**
 * Desktop version chip — only renders inside the Electron shell.
 *  • Neutral slate:   "Desktop · v1.0.0"
 *  • Sky (downloading): animated spinner + percent
 *  • Amber (ready):   "Update ready · v1.0.1" — click-to-restart hint
 * No-op on the web deployment (auto-hidden via the preload-bridge guard).
 *
 * `canManageChannel` (admin/super_admin only) additionally shows a staged-
 * rollout control: flip this one terminal to the "beta" update channel to
 * validate a release before promoting it to "stable" everywhere else.
 */
export default function DesktopVersionChip({ canManageChannel = false }) {
  const bridge = typeof window !== 'undefined' ? window.pharmxai : null;
  const isDesktop = !!bridge?.getAppVersion;

  const [version, setVersion] = useState('');
  const [state, setState] = useState({ status: 'idle' }); // idle | checking | downloading | ready | error
  const [channel, setChannel] = useState('latest');
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!isDesktop) return;
    let unsubscribe = () => {};
    (async () => {
      try {
        const { version: v } = await bridge.getAppVersion();
        setVersion(v || '');
      } catch (e) { console.warn('Desktop version check failed:', e); }
      try {
        if (bridge.getUpdateChannel) {
          const { channel: c } = await bridge.getUpdateChannel();
          setChannel(c || 'latest');
        }
      } catch (e) { console.warn('Desktop update-channel check failed:', e); }
      if (bridge.onUpdateState) {
        unsubscribe = bridge.onUpdateState((s) => {
          setState(s || { status: 'idle' });
          if (s?.channel) setChannel(s.channel);
        });
      }
    })();
    return () => unsubscribe();
  }, [isDesktop, bridge]);

  if (!isDesktop) return null;

  const status = state?.status || 'idle';
  const ready = status === 'ready';
  const downloading = status === 'downloading';
  const checking = status === 'checking';
  const isBeta = channel === 'beta';

  const tone = ready
    ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
    : downloading
    ? 'bg-sky-100 text-sky-800 border-sky-300'
    : isBeta
    ? 'bg-violet-100 text-violet-800 border-violet-300 hover:bg-violet-200'
    : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200';

  const label = ready
    ? `Update ready · v${state.newVersion || ''}`
    : downloading
    ? `Downloading${state.percent != null ? ` · ${state.percent}%` : '…'}`
    : checking
    ? 'Checking…'
    : `Desktop · v${version}${isBeta ? ' · beta' : ''}`;

  const Icon = ready ? Download : downloading || checking ? Loader2 : isBeta ? FlaskConical : Monitor;
  const iconClass = (downloading || checking) ? 'animate-spin' : '';

  const chip = (
    <div
      className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors select-none ${tone} ${canManageChannel ? 'cursor-pointer' : ''}`}
      title={ready ? 'A new version has been downloaded. Restart DutiLyne to install it.' : 'DutiLyne desktop app'}
      data-testid="desktop-version-chip"
    >
      <Icon className={`h-3 w-3 ${iconClass}`} />
      <span>{label}</span>
    </div>
  );

  if (!canManageChannel) return chip;

  const switchChannel = async (next) => {
    if (next === channel || switching) return;
    setSwitching(true);
    try {
      const res = await bridge.setUpdateChannel(next);
      setChannel(res?.channel || next);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button type="button" data-testid="desktop-version-chip-trigger">{chip}</button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Update channel (this terminal only)</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="update-channel-stable" disabled={switching} onClick={() => switchChannel('latest')}>
          <Monitor className="h-3.5 w-3.5 mr-2" /> Stable {!isBeta && '· current'}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="update-channel-beta" disabled={switching} onClick={() => switchChannel('beta')}>
          <FlaskConical className="h-3.5 w-3.5 mr-2" /> Beta (test releases here first) {isBeta && '· current'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
